import React, {useContext, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLongArrowAltLeft, faLongArrowAltRight} from "@fortawesome/free-solid-svg-icons";

// Utils
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {prettyDateWithDayInUserTimezone} from "../../utils/helpers.js";
import linkifyText from "./linkifyText.js";

// Style
import {voice} from "../../style/components/typography.js";
import {border, colors, pad, radius} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {Button, Small, Text, scrollbar} from "../../style/components/general.js";

const TicketNotesSidebar = ({ticket, noteDraft, setNoteDraft, onAddNote}) => {
  const {atLeast, roleCanAccessResource} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);

  const canCompose =
    atLeast("super") && ticket.open && roleCanAccessResource("ticket", "update");

  const [open, setOpen] = useState(
    () => ticket.notes?.length > 0 || (atLeast("super") && ticket.open)
  );

  if (!(ticket.notes?.length > 0 || canCompose)) return null;

  return (
    <NotesDrawer $open={open}>
      <NotesToggle
        type="button"
        title={open ? "Hide notes" : "Show notes"}
        data-testid="ticket.notesToggle"
        $open={open}
        onClick={() => setOpen(prev => !prev)}>
        <FontAwesomeIcon icon={open ? faLongArrowAltRight : faLongArrowAltLeft} />
        <span>Notes{ticket.notes?.length ? ` (${ticket.notes.length})` : ""}</span>
      </NotesToggle>
      <NotesPanel $open={open} data-testid="ticket.notesDrawer">
        {canCompose && (
          <NotesComposer>
            <NoteInput
              data-testid="ticket.note"
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
              placeholder="Add a note (Monday / GitHub link)..."
              rows={3}
            />
            {ticket.accepted && (
              <Button
                type="button"
                title="Add Note"
                onClick={onAddNote}
                disabled={!noteDraft.trim()}>
                Add Note
              </Button>
            )}
          </NotesComposer>
        )}
        {ticket.notes?.length > 0 && (
          <NoteList>
            {ticket.notes.map(note => (
              <NoteItem key={note.id}>
                <Text>{linkifyText(note.body)}</Text>
                <Small>
                  {note.createdBy?.firstName} {note.createdBy?.lastName}
                  &nbsp;·&nbsp;
                  {prettyDateWithDayInUserTimezone(
                    note.createdAt,
                    settings.timezone,
                    "ddd, MMM D YYYY"
                  )}
                </Small>
              </NoteItem>
            ))}
          </NoteList>
        )}
      </NotesPanel>
    </NotesDrawer>
  );
};

TicketNotesSidebar.propTypes = {
  ticket: PropTypes.object.isRequired,
  noteDraft: PropTypes.string.isRequired,
  setNoteDraft: PropTypes.func.isRequired,
  onAddNote: PropTypes.func.isRequired
};

const NotesDrawer = styled.div`
  position: relative;
  flex: 0 0 auto;
  align-self: stretch;
  ${flex("column", "nowrap", "start", "stretch")};
  min-height: 0;
  max-height: 100%;
  width: ${({$open}) => ($open ? "320px" : `${pad * 3.5}px`)};
  border: ${border} solid ${colors.grayLight};
  border-radius: ${radius};
  background: ${colors.grayLight};
  transition: width 0.2s ease;
`;

const NotesToggle = styled.button`
  gap: ${pad}px;
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: ${({theme}) => theme.secondary};
  cursor: pointer;
  ${voice.quiet};
  font-weight: bold;
  white-space: nowrap;

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  ${({$open}) =>
    $open
      ? css`
          ${flex("row", "nowrap", "start", "center")};
          width: 100%;
          padding: ${pad / 2}px ${pad}px;
        `
      : css`
          ${flex("column", "nowrap", "start", "center")};
          flex: 1;
          width: 100%;
          height: 100%;
          min-height: 0;
          padding: ${pad}px ${pad / 2}px;

          span {
            writing-mode: vertical-rl;
            transform: rotate(180deg);
            letter-spacing: 0.04em;
          }
        `}
`;

const NotesPanel = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: ${({$open}) => ($open ? "100%" : "0")};
  opacity: ${({$open}) => ($open ? 1 : 0)};
  visibility: ${({$open}) => ($open ? "visible" : "hidden")};
  padding: ${({$open}) => ($open ? `0 ${pad}px ${pad}px` : "0")};
  transition: opacity 0.2s ease, padding 0.2s ease;
`;

const NotesComposer = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  flex: 0 0 auto;
  gap: ${pad / 2}px;
  margin-bottom: ${pad}px;
`;

const NoteList = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  flex: 1;
  min-height: 0;
  gap: ${pad}px;
  overflow-x: hidden;
  overflow-y: auto;
  ${scrollbar};

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.blue};
    border-color: ${colors.grayLight};
  }
`;

const NoteItem = styled.div`
  padding: ${pad}px;
  border-radius: ${radius};
  background: ${({theme}) => theme.tertiary};
  color: ${colors.blue};

  ${Text}, ${Small}, a {
    color: ${colors.blue};
  }
`;

const NoteInput = styled.textarea`
  ${voice.quiet};
  width: 100%;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  background: ${({theme}) => theme.tertiary};
  color: ${({theme}) => theme.secondary};
  resize: vertical;
  ${scrollbar};

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.blue};
    border-color: ${({theme}) => theme.tertiary};
  }
`;

export default TicketNotesSidebar;
